import styled, { css } from 'styled-components';

import { ButtonProps, ButtonVariant, ButtonColor, getCorTexto } from '.';
import { colorSemanticsArray } from '@/styles/theme';

export type TextoProps = {
  $variant: ButtonVariant;
  $highContrast: boolean;
  $size: number;
  $color: ButtonColor;
};

export const Container = styled.button<ButtonProps>`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${({ $loading }) => {
    if (!$loading) return '';
    return css`
      flex: 1;
      justify-content: center;
    `;
  }}

  background-color: ${({
    theme,
    $color,
    $variant,
    $highContrast,
    $active,
    $loading,
  }) => {
    if ($loading) return theme.colors.neutral.neutral_alpha[3];
    if (['ghost', 'outline'].includes($variant)) {
      if ($active) {
        if (colorSemanticsArray.includes($color))
          return theme.colors.semantic[`${$color}_alpha`][3];
        return theme.colors[$color][`${$color}_alpha`][3];
      }
      return 'transparent';
    }

    if (colorSemanticsArray.includes($color))
      return theme.colors.semantic[$color][$highContrast ? 12 : 8];

    if ($variant === 'soft')
      return theme.colors[$color][`${$color}_alpha`]['3'];
    if (colorSemanticsArray.includes($color))
      return theme.colors.semantic[$color][$highContrast ? 12 : 7];
    return theme.colors[$color][$color][$highContrast ? 12 : 9];
  }};
  border: ${({ theme, $color, $variant }) => {
    if ($variant === 'outline')
      return `1px solid ${theme.colors[$color][`${$color}_alpha`]['8']}`;
    return 'none';
  }};
  ${({ theme, $size }) => {
    return css`
      border-radius: ${theme.radius[$size + 1]}px;
      padding-top: ${theme.spacing[$size]}px;
      padding-right: ${theme.spacing[$size + 1]}px;
      padding-bottom: ${theme.spacing[$size]}px;
      padding-left: ${theme.spacing[$size + 1]}px;
      gap: ${theme.spacing[$size + 1]}px;
    `;
  }}

  ${({ theme, $size }) => {
    const tamanhoTexto = $size + 1;
    return css`
      font-size: ${theme.typography.font.size[tamanhoTexto.toString()]}px;
      font-family: var(--font-${theme.typography.font.family.text});
      font-weight: ${theme.typography.font.weight.medium};
      line-height: ${theme.typography.font.line_height[
        tamanhoTexto.toString()
      ]}px;
    `;
  }};

  color: ${({ theme, $highContrast, $variant, $color }) =>
    getCorTexto({ $color, $highContrast, $variant, theme })};

  white-space: nowrap;
  overflow: hidden;

  &:hover {
    ${({
      theme,
      $color,
      $variant,
      $disable,
      $active,
      $loading,
      $highContrast,
    }) => {
      if ($disable || $loading || $active) return '';

      const recuperarCor = () => {
        if ($variant === 'ghost') {
          return theme.colors.neutral.neutral_alpha['3'];
        } else if ($variant === 'outline') {
          return theme.colors.neutral.neutral_alpha['2'];
        } else if ($variant === 'soft') {
          return theme.colors[$color][`${$color}_alpha`]['4'];
        } else if (colorSemanticsArray.includes($color)) {
          return theme.colors.semantic[$color][$highContrast ? 12 : 7];
        }

        return theme.colors[$color][$color]['10'];
      };

      const cor = recuperarCor();

      return css`
        cursor: pointer;
        background-color: ${cor};
      `;
    }};
  }

  ${({ theme, $disable, $variant }) => {
    if (!$disable || ['ghost', 'outline'].includes($variant)) return '';
    return css`
      background-color: ${theme.colors.neutral.neutral_alpha[
        $variant === 'surface' ? 2 : 3
      ]};

      color: ${theme.colors.neutral.neutral_alpha[8]};
    `;
  }}
`;
